import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { GradientBlobs } from "@/components/GradientBlobs";
import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/SectionHeading";
import { ScrollColorHeading } from "@/components/ScrollColorHeading";
import { ProjectCard } from "@/components/ProjectCard";
import { CTASection } from "@/components/CTASection";
import { PROJECTS } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const Block = ({ label, children }) => (
  <Reveal>
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-b border-brand-plum/10">
      <div className="md:col-span-4">
        <SectionLabel dark>{label}</SectionLabel>
      </div>
      <div className="md:col-span-8 text-brand-plum/75 text-lg md:text-xl leading-relaxed">{children}</div>
    </div>
  </Reveal>
);

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  useSEO({
    title: project ? `${project.title} | Tangensis Business Solutions` : "Project | Tangensis Business Solutions",
    description: project?.excerpt,
    path: project ? `/projects/${project.slug}` : "/projects",
  });

  if (!project) return <Navigate to="/projects" replace />;
  const related = PROJECTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <>
      <section className="relative bg-white noise-overlay pt-40 md:pt-52 pb-16" data-testid="project-detail-hero">
        <GradientBlobs />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <Link to="/projects" data-testid="back-to-projects" className="inline-flex items-center gap-2 text-brand-ink/75 hover:text-brand-primary transition-colors duration-300">
            <ArrowLeft className="h-4 w-4" /> ALL PROJECTS
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="text-xs font-semibold tracking-[0.2em] text-brand-ink bg-brand-ink/10 rounded-full px-4 py-2">{project.category}</span>
            <span className="text-brand-ink/75">{project.year}</span>
            {project.client && <span className="text-brand-ink/75">·&nbsp;&nbsp;{project.client}</span>}
            {project.duration && <span className="text-brand-ink/75">·&nbsp;&nbsp;{project.duration}</span>}
          </div>
          <h1 className="mt-6 font-heading font-extrabold text-brand-ink text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight gradient-text">{project.title}</h1>
        </div>
      </section>

      <section className="bg-brand-offwhite">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 -mt-8 md:-mt-12">
          <Reveal>
            <div className="overflow-hidden rounded-2xl aspect-[16/9] shadow-2xl shadow-brand-magenta/10">
              <img src={project.image} alt={project.title} className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
        {project.metrics && (
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24">
            <Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8" data-testid="project-metrics">
                {project.metrics.map((m, i) => (
                  <div key={i} className="rounded-2xl border border-brand-ink/10 bg-white p-8 text-center md:text-left">
                    <div className="font-display font-black text-5xl md:text-6xl gradient-text leading-none">{m.value}</div>
                    <p className="mt-4 text-brand-ink/70 leading-snug">{m.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        )}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <Block label="Overview">{project.overview}</Block>
          <Block label="Challenge">{project.challenge}</Block>
          <Block label="Strategy">{project.strategy}</Block>
          <Block label="Solution">{project.solution}</Block>
          <Block label="Results">
            <ul className="space-y-3">
              {project.results.map((r, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full gradient-bg shrink-0" />{r}
                </li>
              ))}
            </ul>
          </Block>

          <div className="mt-16">
            <SectionLabel dark>Gallery</SectionLabel>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.gallery.map((g, i) => (
                <Reveal key={i} delay={(i % 2) * 0.1}>
                  <div className="overflow-hidden rounded-2xl aspect-[4/3]">
                    <img src={g} alt={`${project.title} ${i + 1}`} loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white noise-overlay relative">
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="flex items-end justify-between gap-6">
            <ScrollColorHeading as="h2" text="Related Projects" className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight" />
            <Link to="/projects" className="hidden sm:inline-flex items-center gap-2 text-brand-ink/70 hover:text-brand-primary transition-colors duration-300">VIEW ALL <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            {related.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
          </div>
        </div>
      </section>

      <CTASection heading={["INSPIRED BY THIS?", "LET'S TALK."]} />
    </>
  );
}
